import { Maximize2, Minimize2, Moon, Search, Sun } from 'lucide-react';
import { useTheme } from '@/app/providers';
import { Commentable } from '@/shared/comments/Commentable';
import { KbdKey } from '@/design-system/KbdKey';
import { BrandMark } from '@/versions/v1/shared/BrandMark';
import { useBrand } from '@/versions/v1/shared/BrandContext';
import {
  orgLabel,
  useOemPersona,
  type OemPersonaId,
} from '@/versions/v1/shared/OemPersonaContext';

// =========================================================================
// OemTopBar — the demo's `header.top`. Fork-owned (zone C).
//
// Rendered inside the scene column by OemShell, not as app-level chrome: the
// foundation already owns a vertical review rail called TopBar (the comment /
// version / persona strip on the right edge). Keeping this one inside the
// scene avoids fighting that, and keeps the whole OEM chrome removable at the
// two layout.tsx seam lines.
//
// Left to right, matching the demo: lockup · fleet chip · ⌘K search ·
// full width · VIEWING AS · theme toggle. Full width's state lives in
// OemShell (`useFullWidth`), which also binds the F key.
// =========================================================================

/** The palette listens for this; the foundation's ThreadSpotlight used it too. */
function openPalette() {
  window.dispatchEvent(new CustomEvent('app:open-palette'));
}

export type OemTopBarProps = {
  fullWidth: boolean;
  onToggleFullWidth: () => void;
};

export function OemTopBar({ fullWidth, onToggleFullWidth }: OemTopBarProps) {
  const { theme, toggleTheme } = useTheme();
  const { brand } = useBrand();
  const { personaId, personas, setPersona } = useOemPersona();

  return (
    <Commentable name="OemTopBar">
      <header
        className="flex h-12 flex-shrink-0 items-center gap-3 px-4"
        style={{ borderBottom: '1px solid var(--hairline)' }}
      >
        {/* The lockup never shrinks — a clipped client logo is worse than a
            dropped chip, so everything else gives way first. */}
        <BrandMark variant="lockup" className="flex-shrink-0 text-sm" logoHeight={20} />

        <span
          className="hidden flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-1 text-xs xl:inline-flex"
          style={{
            border: '1px solid var(--hairline)',
            background: 'var(--surface-1)',
            color: 'var(--ink-secondary)',
          }}
        >
          <b className="font-semibold">Australian fleet</b>
          <span
            className="rounded px-1 py-px text-[10px] uppercase tracking-[0.06em]"
            style={{ background: 'var(--surface-3)', color: 'var(--ink-tertiary)' }}
          >
            Service
          </span>
        </span>

        <span className="flex-1" aria-hidden="true" />

        <button
          type="button"
          onClick={openPalette}
          className="hidden min-w-0 flex-shrink items-center gap-2 overflow-hidden whitespace-nowrap rounded-lg px-2.5 py-1.5 text-xs transition-colors hover:bg-zinc-900/[0.04] md:inline-flex dark:hover:bg-white/[0.04]"
          style={{
            border: '1px solid var(--hairline)',
            background: 'var(--surface-1)',
            color: 'var(--ink-tertiary)',
          }}
          aria-label="Search machines, work orders, parts and data"
          aria-keyshortcuts="Meta+K Control+K"
        >
          <Search className="h-3.5 w-3.5" aria-hidden="true" />
          <span className="hidden truncate 2xl:inline">
            Search machines, work orders, parts, data…
          </span>
          <span className="2xl:hidden">Search</span>
          <span className="ml-1 flex flex-shrink-0 items-center gap-0.5">
            <KbdKey>⌘</KbdKey>
            <KbdKey>K</KbdKey>
          </span>
        </button>

        <button
          type="button"
          onClick={onToggleFullWidth}
          className="inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 py-1.5 text-xs transition-colors hover:bg-zinc-900/[0.04] dark:hover:bg-white/[0.04]"
          style={{
            border: '1px solid var(--hairline)',
            background: fullWidth ? 'var(--surface-3)' : 'var(--surface-1)',
            color: 'var(--ink-secondary)',
          }}
          aria-pressed={fullWidth}
          aria-keyshortcuts="F"
          title={
            fullWidth
              ? 'Exit full width (F) — bring back the menu and the assistant'
              : 'Full width (F) — hide the menu and the assistant'
          }
        >
          {fullWidth ? (
            <Minimize2 className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <Maximize2 className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          <span className="hidden lg:inline">{fullWidth ? 'Exit full width' : 'Full width'}</span>
          <span className="sr-only lg:hidden">{fullWidth ? 'Exit full width' : 'Full width'}</span>
        </button>

        <label className="flex flex-shrink-0 items-center gap-1.5">
          <span
            className="hidden whitespace-nowrap text-[10px] font-medium uppercase tracking-[0.08em] xl:inline"
            style={{ color: 'var(--ink-tertiary)' }}
          >
            Viewing as
          </span>
          <select
            value={personaId}
            onChange={(e) => setPersona(e.target.value as OemPersonaId)}
            className="max-w-[200px] rounded-md px-2 py-1 text-xs outline-none focus:border-input-focus"
            style={{
              border: '1px solid var(--hairline)',
              background: 'var(--surface-1)',
              color: 'var(--ink-primary)',
            }}
            aria-label="Viewing as"
          >
            {/* Job title only in the closed control — a <select> renders the
                selected option's full text and the org line overflows it. The
                org and remit ride along as the option's tooltip. */}
            {personas.map((p) => (
              <option
                key={p.id}
                value={p.id}
                title={`${orgLabel(p, brand.lockup)} — ${p.desc}`}
              >
                {p.name}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          onClick={toggleTheme}
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md transition-colors hover:bg-zinc-900/[0.04] dark:hover:bg-white/[0.04]"
          style={{ color: 'var(--ink-secondary)' }}
          aria-label={
            theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme'
          }
        >
          {theme === 'light' ? (
            <Moon className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Sun className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
      </header>
    </Commentable>
  );
}
